import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const contactJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${siteUrl}/contact`,
  url: `${siteUrl}/contact`,
  name: 'お問い合わせ｜特注ネジ・難削材加工のご相談｜おかやまネジ製作所',
  description: '岡山市おかやまネジ製作所へのお問い合わせページ。特注ネジ・難削材加工・小ロット対応のご相談はこちら。電話：086-012-3456。2営業日以内にご返信。',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'トップ', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'お問い合わせ', item: `${siteUrl}/contact` },
    ],
  },
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'おかやまネジ製作所',
    telephone: '+81-86-012-3456',
    faxNumber: '+81-86-012-3457',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北区○○町1-2-3',
      addressLocality: '岡山市',
      addressRegion: '岡山県',
      postalCode: '700-0000',
      addressCountry: 'JP',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
  },
};

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = 'お問い合わせ｜特注ネジ・難削材加工のご相談｜おかやまネジ製作所';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '岡山市おかやまネジ製作所へのお問い合わせページ。特注ネジ・難削材加工・小ロット対応のご相談はこちら。電話：086-012-3456。2営業日以内にご返信。');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/contact`);

    const scriptId = 'contact-jsonld';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(contactJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6fttlpghdq4qda6vu20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <PageHero
          title="お問い合わせ"
          breadcrumbs={[
            { label: 'トップ', path: '/' },
            { label: 'お問い合わせ' },
          ]}
          imageUrl="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ade13bbbcea5f91fc8d9f8759c0b1182.jpeg"
        />

        {/* お問い合わせフォーム */}
        <section className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-navy text-center mb-6 sm:mb-8">
              お問い合わせフォーム
            </h2>
            <p className="text-base sm:text-lg text-gray-600 text-center mb-10 sm:mb-12 lg:mb-16">
              ご質問・ご相談など、お気軽にお問い合わせください。<br />
              2営業日以内にご返信いたします。
            </p>

            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm sm:text-base font-bold text-navy mb-2">
                  お名前 <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 sm:py-4 border border-navy rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/50 text-sm sm:text-base"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm sm:text-base font-bold text-navy mb-2">
                  会社名
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 sm:py-4 border border-navy rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/50 text-sm sm:text-base"
                  placeholder="株式会社○○"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm sm:text-base font-bold text-navy mb-2">
                  メールアドレス <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 sm:py-4 border border-navy rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/50 text-sm sm:text-base"
                  placeholder="example@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm sm:text-base font-bold text-navy mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 sm:py-4 border border-navy rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/50 text-sm sm:text-base"
                  placeholder="086-012-3456"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm sm:text-base font-bold text-navy mb-2">
                  お問い合わせ内容 <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={8}
                  className="w-full px-4 py-3 sm:py-4 border border-navy rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/50 resize-none text-sm sm:text-base"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                  {formData.message.length} / 500文字
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-500 text-green-700 px-4 py-3 rounded-lg text-sm sm:text-base">
                  お問い合わせを受け付けました。ご連絡ありがとうございます。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm sm:text-base">
                  送信に失敗しました。もう一度お試しください。
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-navy text-white px-8 py-4 sm:py-5 rounded-lg text-base sm:text-lg font-bold hover:bg-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;